import { useState, useEffect, useCallback } from 'react';
import { Form, Input, Button, Card, InputNumber, Space, Typography, message, Spin, Row, Col } from 'antd';
import { SaveOutlined, BankOutlined, PhoneOutlined, MailOutlined, GlobalOutlined, InstagramOutlined, LinkedinOutlined, FacebookOutlined } from '@ant-design/icons';
import { companyAPI } from '../services/api';

const { Title, Text } = Typography;
const { TextArea } = Input;

export default function Company() {
    const [form] = Form.useForm();
    const [loading, setLoading] = useState(true);
    const [submitting, setSubmitting] = useState(false);
    const [companyId, setCompanyId] = useState(null);

    const fetchCompany = useCallback(async () => {
        try {
            setLoading(true);
            const response = await companyAPI.get();
            console.log('Company API response:', response);

            if (response.data) {
                const data = response.data;
                console.log('Company data received:', data);

                // Store company ID for updates
                setCompanyId(data.id);

                // Flatten nested objects for form
                const formValues = {
                    name: data.name || '',
                    tagline: data.tagline || '',
                    description: data.description || '',
                    logo: data.logo || '',
                    vision: data.vision || '',
                    mission: data.mission || [],
                    address: data.address || '',
                    phone: data.phone || '',
                    email: data.email || '',
                    whatsapp: data.whatsapp || '',
                    'socialMedia.instagram': data.socialMedia?.instagram || '',
                    'socialMedia.linkedin': data.socialMedia?.linkedin || '',
                    'socialMedia.facebook': data.socialMedia?.facebook || '',
                    'stats.projectsCompleted': data.stats?.projectsCompleted || 0,
                    'stats.teamMembers': data.stats?.teamMembers || 0,
                    'stats.awards': data.stats?.awards || 0,
                    'stats.satisfiedClients': data.stats?.satisfiedClients || 0,
                };
                console.log('Setting form values:', formValues);
                form.setFieldsValue(formValues);
            } else {
                console.error('No data in response');
                message.error('No company data received from server');
            }
        } catch (error) {
            console.error('Error fetching company data:', error);
            console.error('Error response:', error.response);
            message.error('Error: ' + (error.response?.data?.message || error.message));
        } finally {
            setLoading(false);
        }
    }, [form]);

    useEffect(() => {
        fetchCompany();
    }, [fetchCompany]);

    const handleSubmit = async (values) => {
        setSubmitting(true);
        try {
            // Check if we have company ID
            if (!companyId) {
                message.error('Company ID not found. Please refresh the page.');
                setSubmitting(false);
                return;
            }

            // Restructure nested fields
            const formData = {
                ...values,
                socialMedia: {
                    instagram: values['socialMedia.instagram'] || '',
                    linkedin: values['socialMedia.linkedin'] || '',
                    facebook: values['socialMedia.facebook'] || '',
                },
                stats: {
                    projectsCompleted: values['stats.projectsCompleted'] || 0,
                    teamMembers: values['stats.teamMembers'] || 0,
                    awards: values['stats.awards'] || 0,
                    satisfiedClients: values['stats.satisfiedClients'] || 0,
                },
            };

            console.log('Updating company with ID:', companyId);
            console.log('Form data:', formData);
            await companyAPI.update(companyId, formData);
            message.success('Company information updated successfully!');
            fetchCompany();
        } catch (error) {
            console.error('Update error:', error);
            message.error('Error: ' + (error.response?.data?.message || error.message));
        } finally {
            setSubmitting(false);
        }
    };

    if (loading) {
        return <div style={{ textAlign: 'center', padding: '50px' }}><Spin size="large" /></div>;
    }

    return (
        <div style={{ maxWidth: 1200, margin: '0 auto' }}>
            <div style={{ marginBottom: 24 }}>
                <Title level={2}><BankOutlined /> Company Information</Title>
                <Text type="secondary">Manage your company details and contact information</Text>
            </div>

            <Form form={form} layout="vertical" onFinish={handleSubmit}>
                {/* Basic Information */}
                <Card title="🏢 Basic Information" style={{ marginBottom: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                    <Row gutter={16}>
                        <Col xs={24} md={12}>
                            <Form.Item name="name" label="Company Name" rules={[{ required: true }]}>
                                <Input size="large" prefix={<BankOutlined />} placeholder="PT. Company Name" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={12}>
                            <Form.Item name="tagline" label="Tagline">
                                <Input size="large" placeholder="Your company tagline" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item name="description" label="Description">
                        <TextArea rows={4} placeholder="Company description..." />
                    </Form.Item>
                    <Form.Item name="logo" label="Logo URL">
                        <Input size="large" prefix={<GlobalOutlined />} placeholder="https://example.com/logo.png" />
                    </Form.Item>
                </Card>

                {/* Vision & Mission */}
                <Card title="🎯 Vision & Mission" style={{ marginBottom: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                    <Form.Item name="vision" label="Vision">
                        <TextArea rows={3} placeholder="Company vision..." />
                    </Form.Item>
                    <Form.List name="mission">
                        {(fields, { add, remove }) => (
                            <>
                                {fields.map((field, index) => (
                                    <Space key={field.key} style={{ display: 'flex', marginBottom: 8 }} align="baseline">
                                        <Form.Item {...field} label={`Mission ${index + 1}`} style={{ marginBottom: 0, flex: 1 }}>
                                            <Input size="large" placeholder="Mission statement" />
                                        </Form.Item>
                                        {fields.length > 1 && (
                                            <Button danger onClick={() => remove(field.name)}>Remove</Button>
                                        )}
                                    </Space>
                                ))}
                                <Button type="dashed" onClick={() => add()} block>+ Add Mission</Button>
                            </>
                        )}
                    </Form.List>
                </Card>

                {/* Contact Information */}
                <Card title="📞 Contact Information" style={{ marginBottom: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                    <Form.Item name="address" label="Address" rules={[{ required: true }]}>
                        <TextArea rows={3} placeholder="Company address..." />
                    </Form.Item>
                    <Row gutter={16}>
                        <Col xs={24} md={8}>
                            <Form.Item name="phone" label="Phone">
                                <Input size="large" prefix={<PhoneOutlined />} placeholder="+62 21 1234 5678" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item name="email" label="Email" rules={[{ type: 'email' }]}>
                                <Input size="large" prefix={<MailOutlined />} placeholder="info@company.com" />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item name="whatsapp" label="WhatsApp">
                                <Input size="large" prefix={<PhoneOutlined />} placeholder="+62 812 3456 7890" />
                            </Form.Item>
                        </Col>
                    </Row>
                </Card>

                {/* Social Media */}
                <Card title="🌐 Social Media" style={{ marginBottom: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                    <Row gutter={16}>
                        <Col xs={24} md={8}>
                            <Form.Item name="socialMedia.instagram" label="Instagram">
                                <Input size="large" prefix={<InstagramOutlined />} placeholder="https://instagram.com/..." />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item name="socialMedia.linkedin" label="LinkedIn">
                                <Input size="large" prefix={<LinkedinOutlined />} placeholder="https://linkedin.com/company/..." />
                            </Form.Item>
                        </Col>
                        <Col xs={24} md={8}>
                            <Form.Item name="socialMedia.facebook" label="Facebook">
                                <Input size="large" prefix={<FacebookOutlined />} placeholder="https://facebook.com/..." />
                            </Form.Item>
                        </Col>
                    </Row>
                </Card>

                {/* Statistics */}
                <Card title="📊 Statistics" style={{ marginBottom: 16, boxShadow: '0 2px 8px rgba(0,0,0,0.1)' }}>
                    <Row gutter={16}>
                        <Col xs={24} sm={12} md={6}>
                            <Form.Item name="stats.projectsCompleted" label="Projects Completed">
                                <InputNumber size="large" min={0} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Form.Item name="stats.teamMembers" label="Team Members">
                                <InputNumber size="large" min={0} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Form.Item name="stats.awards" label="Awards">
                                <InputNumber size="large" min={0} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col xs={24} sm={12} md={6}>
                            <Form.Item name="stats.satisfiedClients" label="Satisfied Clients">
                                <InputNumber size="large" min={0} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>
                </Card>

                {/* Submit Button */}
                <Form.Item style={{ marginTop: 32 }}>
                    <Button
                        type="primary"
                        size="large"
                        htmlType="submit"
                        icon={<SaveOutlined />}
                        loading={submitting}
                        block
                        style={{
                            background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                            border: 'none',
                            height: 48
                        }}
                    >
                        Save Company Information
                    </Button>
                </Form.Item>
            </Form>
        </div>
    );
}
